import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/common/PageLayout';
import { useAuth } from '../../hooks/useAuth';
import './About.css';

/**
 * Modern About Us Page - Karadaş Hukuk Bürosu
 *
 * Sections:
 * 1. Hero Image Section - Full width with overlay
 * 2. About Content - 2 Column Grid (Image Left, Text Right)
 * 3. Core Values - 3 Card Layout
 * 4. Founder Profile - Glassmorphism Card
 * 5. CTA Section - Contact Button
 */
const About: React.FC = () => {
  const { user } = useAuth();

  return (
    <PageLayout kind="about" title="Hakkımızda">
      {/* ABOUT CONTENT - 2 Column Grid */}
      <section className="about-hero">
        <div className="about-container">
          <div className="hero-grid">
            {/* Left Column - Image */}
            <div className="hero-image-wrapper">
              <img
                src="/gorseller/abi.jpeg"
                alt="Av. Sinan Karadaş"
                className="hero-image"
              />
            </div>

            {/* Right Column - Text */}
            <div className="hero-content">
              <h1 className="hero-title">Hakkımızda</h1>
              <div className="hero-text">
                <p>
                  <strong>Karadaş Hukuk Bürosu</strong> olarak; müvekkillerimize özenli, şeffaf ve sonuç odaklı
                  hizmet sunuyoruz. Ceza, aile, iş ve ticaret hukuku başta olmak üzere geniş bir yelpazede
                  danışmanlık ve avukatlık faaliyetleri yürütmekteyiz.
                </p>
                <p>
                  Adaletin ve hukukun üstünlüğüne olan inancımızla, her müvekkilimize özel çözümler
                  üreterek en iyi hukuki desteği sağlamayı hedefliyoruz. Deneyimli kadromuz ve güncel
                  mevzuat takibimiz ile hukuki süreçlerinizde yanınızdayız.
                </p>
                <p>
                  Profesyonel yaklaşımımız, güçlü iletişimimiz ve müvekkil memnuniyeti odaklı
                  çalışma prensiplerimiz ile hukuki ihtiyaçlarınıza en uygun çözümleri sunuyoruz.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE VALUES SECTION - 3 Cards */}
      <section className="about-values">
        <div className="about-container">
          <h2 className="section-title">Temel Değerlerimiz</h2>
          <div className="values-grid">
            {/* Card 1 - Transparency */}
            <div className="value-card">
              <div className="value-icon">
                <i className="bi bi-eye"></i>
              </div>
              <h3 className="value-title">Şeffaflık</h3>
              <p className="value-description">
                Müvekkillerimizle açık ve net iletişim kurar, tüm süreçleri şeffaf bir
                şekilde yönetiriz. Güven, çalışmalarımızın temelidir.
              </p>
            </div>

            {/* Card 2 - Results-Oriented */}
            <div className="value-card">
              <div className="value-icon">
                <i className="bi bi-trophy"></i>
              </div>
              <h3 className="value-title">Sonuç Odaklı</h3>
              <p className="value-description">
                Her davada en iyi sonucu elde etmek için titizlikle çalışırız. Stratejik
                yaklaşımımız ve deneyimimiz ile başarıya ulaşırız.
              </p>
            </div>

            {/* Card 3 - Wide Expertise */}
            <div className="value-card">
              <div className="value-icon">
                <i className="bi bi-briefcase"></i>
              </div>
              <h3 className="value-title">Geniş Uzmanlık</h3>
              <p className="value-description">
                Ceza, aile, iş ve ticaret hukuku gibi birçok alanda uzman kadromuz ile
                kapsamlı hukuki destek sağlıyoruz.
              </p>
            </div>

            {/* Card 4 - Professionalism */}
            <div className="value-card">
              <div className="value-icon">
                <i className="bi bi-patch-check"></i>
              </div>
              <h3 className="value-title">Profesyonellik</h3>
              <p className="value-description">
                En yüksek etik standartlara uygun, disiplinli ve profesyonel bir yaklaşımla
                hukuki hizmet sunuyoruz.
              </p>
            </div>

            {/* Card 5 - Innovation */}
            <div className="value-card">
              <div className="value-icon">
                <i className="bi bi-lightbulb"></i>
              </div>
              <h3 className="value-title">Yenilikçilik</h3>
              <p className="value-description">
                Modern hukuki çözümler ve güncel teknolojilerle müvekkillerimize daha
                etkili ve verimli hizmet sağlıyoruz.
              </p>
            </div>

            {/* Card 6 - Client Focus */}
            <div className="value-card">
              <div className="value-icon">
                <i className="bi bi-heart"></i>
              </div>
              <h3 className="value-title">Müvekkil Memnuniyeti</h3>
              <p className="value-description">
                Her müvekkilimizin ihtiyaçlarını anlar, onlara özel çözümler üreterek
                en yüksek memnuniyeti hedefleriz.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER SECTION - Glassmorphism Profile Card */}
      <section className="about-founder">
        <div className="about-container">
          <div className="founder-card">
            <div className="founder-image-wrapper">
              <img
                src="/gorseller/abi.jpeg"
                alt="Av. Sinan Karadaş"
                className="founder-image"
              />
            </div>
            <div className="founder-content">
              <div className="founder-badge">
                <i className="bi bi-award"></i>
                <span>Kurucu Avukat</span>
              </div>
              <h3 className="founder-name">Av. Sinan Karadaş</h3>
              <p className="founder-title">Kurucu Ortak & Baş Avukat</p>
              <div className="founder-bio">
                <p>
                  Yılların verdiği deneyim ve uzmanlık ile Karadaş Hukuk Bürosu'nu kurarak,
                  müvekkillerimize en üst düzeyde hukuki hizmet sunma vizyonuyla yola çıktım.
                </p>
                <p>
                  Ceza hukuku, aile hukuku, iş hukuku ve ticaret hukuku alanlarındaki derin
                  bilgi birikimim ile her müvekkilime özel çözümler üretiyorum.
                </p>
              </div>
              <div className="founder-stats">
                <div className="stat-item">
                  <i className="bi bi-calendar-check"></i>
                  <div>
                    <strong>5+ Yıl</strong>
                    <span>Deneyim</span>
                  </div>
                </div>
                <div className="stat-item">
                  <i className="bi bi-trophy"></i>
                  <div>
                    <strong>500+</strong>
                    <span>Başarılı Dava</span>
                  </div>
                </div>
                <div className="stat-item">
                  <i className="bi bi-people"></i>
                  <div>
                    <strong>∞</strong>
                    <span>Mutlu Müvekkil</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Google Maps Location */}
          <div className="office-location-section">
            <h2 className="location-title">Ofis Konumumuz</h2>
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1599.2664049862603!2d37.13075044810024!3d36.70976304107403!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x152fd19b5c8a0921%3A0xcabe5b285829e577!2sSinan%20Karada%C5%9F%20Hukuk%20B%C3%BCrosu!5e0!3m2!1str!2str!4v1774455218099!5m2!1str!2str"
                width="100%"
                height="450"
                style={{border:0}}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Sinan Karadaş Hukuk Bürosu Konumu"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION - Call to Action */}
      <section className="about-cta">
        <div className="about-container">
          <div className="cta-content">
            <h2 className="cta-title">Hukuki Desteğe mi İhtiyacınız Var?</h2>
            <p className="cta-subtitle">
              Size en uygun çözümü bulmak için buradayız. Hemen iletişime geçin,
              hukuki süreçlerinizde yanınızda olalım.
            </p>
            {user ? (
              <a href="mailto:sinansakitkaradas@gmail.com" className="cta-button">
                <i className="bi bi-envelope"></i>
                Bizimle İletişime Geçin
              </a>
            ) : (
              <Link to="/login" className="cta-button">
                <i className="bi bi-box-arrow-in-right"></i>
                İletişim İçin Giriş Yapın
              </Link>
            )}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default About;
